import { decorate, observable, action } from 'mobx';

export const SCHEDULES = {
  SCHEDULE_0830: 'SCHEDULE_0830',
  SCHEDULE_1000: 'SCHEDULE_1000',
  SCHEDULE_1400: 'SCHEDULE_1400',
  SCHEDULE_2400: 'SCHEDULE_2400',
};

function getSchedule({ serviceCode, serviceScheduleCode }) {
  switch (serviceCode) {
    case 1: // COURIER
      switch (serviceScheduleCode) {
        case 0: // ASM 10
          return SCHEDULES.SCHEDULE_1000;
        case 2: // ASM 14
          return SCHEDULES.SCHEDULE_1400;
        default:
          return SCHEDULES.SCHEDULE_2400;
      }
    case 30: // ASM0830
      return SCHEDULES.SCHEDULE_0830;
    case 27: // 14H SOBRES
      return SCHEDULES.SCHEDULE_1400;
    default:
      return SCHEDULES.SCHEDULE_2400;
  }
}

function setFallbackLocations({ notGeolocatedAddresses, agencyLocation }) {
  const originLng = agencyLocation.coordinates[0];
  const originLat = agencyLocation.coordinates[1];
  const distanceToOrigin = 0.0005;
  const totalAddresses = notGeolocatedAddresses.length;
  notGeolocatedAddresses.forEach((address, index) => {
    const angle = 360 * index / totalAddresses;
    const angleRadians = angle * Math.PI / 180;
    const deltaLng = Math.sin(angleRadians) * distanceToOrigin;
    const deltaLat = Math.cos(angleRadians) * distanceToOrigin;
    const location = {
      type: 'Point',
      coordinates: [originLng + deltaLng, originLat + deltaLat],
    };
    const locationPrecision = 'unknown';
    Object.assign(address, { location, locationPrecision, notGeolocated: true });
  });
}

export default class RoutePage {
  constructor() {
    this.agency = 0;
    this.agencyLocation = undefined;
    this.driver = 0;
    this.date = '';
    this.code = 0;
    this.editable = false;
    this.stops = [];
    this.notFoundStops = [];
    this.notGeolocatedStops = [];
    this.addresses = [];
    this.addressesByKey = {};
    this.unorderedAddresses = [];
    this.orderedAddresses = [];
    this.orderAtIndex = 0;
  }

  update({ agency, agencyLocation, driver, date, code, editable, stops, addresses, keepOldOrder }) {
    this.agency = agency;
    this.agencyLocation = agencyLocation;
    this.driver = driver;
    this.date = date;
    this.code = code;
    this.editable = editable;
    this.stops = stops.map(stop => Object.assign(stop, { key: `${stop.addressUuid}${stop.addressType}`, schedule: getSchedule(stop) }));
    this.addresses = addresses.map(address => Object.assign(address, { key: `${address.uuid}${address.type}` }));
    const notGeolocatedAddresses = this.addresses.filter(address => address.location === undefined);
    setFallbackLocations({ notGeolocatedAddresses, agencyLocation });
    this.addressesByKey = {};
    this.addresses.forEach((address) => { this.addressesByKey[address.key] = address; });
    const oldOrderedAddresses = this.orderedAddresses;
    this.notFoundStops = [];
    this.notGeolocatedStops = [];
    this.unorderedAddresses = [];
    this.orderedAddresses = [];
    const orderByKey = {};
    if (keepOldOrder) {
      oldOrderedAddresses.forEach(({ key, order }) => { orderByKey[key] = order; });
    } else {
      this.stops.filter(stop => stop.order !== undefined).forEach(({ key, order }) => { orderByKey[key] = order; });
    }
    this.stops.forEach((stop) => {
      const { key, serviceCode, serviceScheduleCode, schedule } = stop;
      const address = this.addressesByKey[key];
      const order = orderByKey[key];
      if (address === undefined) {
        this.notFoundStops.push(stop);
      } else if (address.location === undefined) {
        this.notGeolocatedStops.push(stop);
      } else if (order === undefined) {
        this.unorderedAddresses.push(Object.assign(address, { serviceCode, serviceScheduleCode, schedule }));
      } else {
        this.orderedAddresses.push(Object.assign(address, { serviceCode, serviceScheduleCode, schedule, order }));
      }
      // eslint-disable-next-line no-param-reassign
      delete stop.order;
    });
    this.orderedAddresses = this.orderedAddresses.slice().sort((a, b) => a.order - b.order);
    this.orderedAddresses.forEach((address, index) => Object.assign(address, { order: index + 1 }));
    this.orderAtIndex = this.orderedAddresses.length;
  }

  getStopsToSend() {
    const ordered = this.orderedAddresses.map((address) => {
      const { uuid: addressUuid, type: addressType, serviceCode, serviceScheduleCode, order } = address;
      return { addressUuid, addressType, serviceCode, serviceScheduleCode, order };
    });
    const unordered = this.unorderedAddresses.map((address) => {
      const { uuid: addressUuid, type: addressType, serviceCode, serviceScheduleCode } = address;
      return { addressUuid, addressType, serviceCode, serviceScheduleCode };
    });
    const notFound = this.notFoundStops.map((stop) => {
      const { addressUuid, addressType, serviceCode, serviceScheduleCode } = stop;
      return { addressUuid, addressType, serviceCode, serviceScheduleCode };
    });
    const notGeolocated = this.notGeolocatedStops.map((stop) => {
      const { addressUuid, addressType, serviceCode, serviceScheduleCode } = stop;
      return { addressUuid, addressType, serviceCode, serviceScheduleCode };
    });
    return [...ordered, ...unordered, ...notFound, ...notGeolocated];
  }

  orderAddresses({ keys }) {
    const keysSet = new Set(keys);
    const addressesToOrder = this.unorderedAddresses.filter(address => keysSet.has(address.key));
    if (addressesToOrder.length > 0) {
      const previousAddresses = this.orderedAddresses.slice(0, this.orderAtIndex);
      const nextAddresses = this.orderedAddresses.slice(this.orderAtIndex);
      this.orderedAddresses = [...previousAddresses, ...addressesToOrder, ...nextAddresses];
      this.orderedAddresses.forEach((address, index) => Object.assign(address, { order: index + 1 }));
      this.unorderedAddresses = this.addresses.filter(({ order }) => order === undefined);
      this.orderAtIndex += addressesToOrder.length;
    }
  }

  unorderAddresses({ keys }) {
    const keysSet = new Set(keys);
    const addressesToUnorder = this.orderedAddresses.filter(address => keysSet.has(address.key));
    if (addressesToUnorder.length > 0) {
      this.orderAtIndex -= addressesToUnorder.filter(address => address.order <= this.orderAtIndex).length;
      addressesToUnorder.forEach(address => Object.assign(address, { order: undefined }));
      this.orderedAddresses = this.addresses.filter(({ order }) => order !== undefined).sort((a, b) => a.order - b.order);
      this.orderedAddresses.forEach((address, index) => Object.assign(address, { order: index + 1 }));
      this.unorderedAddresses = this.addresses.filter(({ order }) => order === undefined);
    }
  }

  reorderAddress({ fromIndex: from, toIndex: to }) {
    if (from !== to && from >= 0 && from < this.orderedAddresses.length && to >= 0 && to < this.orderedAddresses.length) {
      const addressToInsert = this.orderedAddresses[from];
      const remainingAddresses = [...this.orderedAddresses.slice(0, from), ...this.orderedAddresses.slice(from + 1)];
      const previousAddresses = remainingAddresses.slice(0, to);
      const nextAddresses = remainingAddresses.slice(to);
      this.orderedAddresses = [...previousAddresses, addressToInsert, ...nextAddresses];
      this.orderedAddresses.forEach((address, index) => Object.assign(address, { order: index + 1 }));
      if (from < this.orderAtIndex && to >= this.orderAtIndex) {
        this.orderAtIndex -= 1;
      } else if (from >= this.orderedAddresses && to < this.orderAtIndex) {
        this.orderAtIndex += 1;
      }
    }
  }
}

decorate(RoutePage, {
  agency: observable,
  driver: observable,
  date: observable,
  editable: observable,
  stops: observable,
  addresses: observable,
  orderedAddresses: observable,
  unorderedAddresses: observable,
  orderAtIndex: observable,
  update: action,
  orderAddresses: action,
  unorderAddresses: action,
  reorderAddress: action,
});

export const routePage = new RoutePage();



// WEBPACK FOOTER //
// ./src/store/route-page.js